import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ArticleCard from "@/components/feature/ArticleCard";
import NewsletterForm from "@/components/feature/NewsletterForm";
import FaqAccordion from "@/components/feature/FaqAccordion";
import { useSeo } from "@/hooks/useSeo";
import { articles, categories, faqs, siteStats, testimonials } from "@/mocks/content";

export default function Home() {
  const featured = articles.find((a) => a.featured) ?? articles[0];
  const hotList = [...articles].sort((a, b) => b.hot - a.hot).slice(0, 5);
  const latest = [...articles]
    .filter((a) => a.slug !== featured.slug)
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, 6);

  useSeo({
    title: "黑料吃话 - 娱乐热点深扒解读与幕后分析",
    description:
      "黑料吃话专注娱乐热点深扒解读，从细节入手还原事件时间线，分析幕后逻辑与人物关系，提供多角度观点，带你吃透每一个瓜。",
    keywords: "黑料吃话,黑料吃话解读,黑料吃话分析,黑料吃话深扒,黑料吃话细节,娱乐热点解读",
    path: "/",
    image: featured.cover,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "黑料吃话",
        url: `${window.location.origin}`,
        description: "娱乐热点深扒解读、幕后逻辑与人物关系分析内容站",
        inLanguage: "zh-CN",
        potentialAction: {
          "@type": "SearchAction",
          target: `${window.location.origin}/topics?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "黑料吃话",
        description: "以深扒解读为核心的娱乐热点评论内容站",
        email: "contact@heiliaochihua.example",
      },
    ],
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative w-full overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=abstract%20editorial%20collage%20of%20warm%20paper%20textures%20with%20terracotta%20and%20amber%20geometric%20shapes%2C%20soft%20film%20grain%2C%20dramatic%20warm%20lighting%2C%20stylized%20minimal%20artistic%20background%2C%20high%20detail%2C%20no%20text&width=1800&height=1000&seq=hlch-hero-main&orientation=landscape"
              alt="暖色抽象纸感拼贴背景，象征深度解读的观察视角"
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/35 to-black/55"></div>
          </div>

          <div className="relative mx-auto flex w-full flex-col items-center px-4 py-20 text-center md:px-8 md:py-28">
            <h1 className="animate-rise font-heading text-4xl font-black leading-tight tracking-tight text-background-50 md:text-6xl">
              黑料吃话
            </h1>
            <p className="animate-rise mt-5 font-heading text-lg text-accent-200 md:text-2xl">
              深扒解读 · 幕后逻辑 · 多角度看热点
            </p>
            <p className="animate-rise mt-6 max-w-2xl text-sm leading-8 text-background-100 md:text-base">
              不做情绪站队，只把散落的传闻、声明、路透与舆论反馈整理成可复盘的时间线。从细节入手，分析事件幕后逻辑与人物关系，带你真正吃透一个瓜。
            </p>
            <div className="animate-rise mt-9 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/topics"
                className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-book-shelf-line"></i>进入话题库
              </Link>
              <Link
                to="/about"
                className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/50 px-7 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/15"
              >
                <i className="ri-information-line"></i>了解我们
              </Link>
            </div>
          </div>

          <div className="relative mx-auto -mb-1 w-full px-4 md:px-8">
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-t-lg bg-background-300 md:grid-cols-4">
              {siteStats.map((stat) => (
                <div key={stat.label} className="flex flex-col items-center bg-background-50 px-4 py-6">
                  <i className={`${stat.icon} text-xl text-primary-600`}></i>
                  <span className="mt-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    {stat.value}
                  </span>
                  <span className="mt-1 text-xs text-foreground-500">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 焦点 + 热榜 */}
        <section className="w-full bg-background-50 py-16 md:py-20">
          <div className="mx-auto w-full px-4 md:px-8">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <div className="mb-6 flex items-center gap-3">
                  <span className="h-6 w-1.5 rounded-full bg-primary-500"></span>
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">本期焦点解读</h2>
                </div>
                <ArticleCard article={featured} variant="wide" />
                <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
                  <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
                    <i className="ri-lightbulb-flash-line text-accent-600"></i>这篇解读的关键切入点
                  </h3>
                  <ul className="mt-4 space-y-3">
                    {featured.points.map((point) => (
                      <li key={point} className="flex items-start gap-3 text-sm leading-7 text-foreground-600">
                        <i className="ri-checkbox-blank-circle-fill mt-2.5 text-[6px] text-primary-500"></i>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <aside className="lg:col-span-4">
                <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                  <div className="mb-5 flex items-center justify-between">
                    <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                      <i className="ri-fire-line text-primary-600"></i>热议榜单
                    </h2>
                    <Link to="/topics" className="cursor-pointer whitespace-nowrap text-xs text-secondary-700 hover:text-primary-600">
                      全部 &gt;
                    </Link>
                  </div>
                  <div className="space-y-3">
                    {hotList.map((article, index) => (
                      <ArticleCard key={article.slug} article={article} variant="compact" index={index} />
                    ))}
                  </div>
                </div>

                <div className="mt-6 rounded-lg bg-secondary-900 p-6 text-center">
                  <i className="ri-discuss-line text-2xl text-accent-300"></i>
                  <h3 className="mt-3 font-heading text-lg font-bold text-background-50">你有不同的看法？</h3>
                  <p className="mt-2 text-sm leading-7 text-secondary-200">
                    欢迎把线索、截图或你的分析角度发给我们，经核实的线索会出现在下一期解读里。
                  </p>
                  <Link
                    to="/contact"
                    className="mt-5 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-950 transition-colors hover:bg-accent-400"
                  >
                    <i className="ri-send-plane-line"></i>提供线索
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* 栏目分类 */}
        <section className="w-full bg-background-100 py-16 md:py-20">
          <div className="mx-auto w-full px-4 md:px-8">
            <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <span className="text-xs font-medium tracking-[0.24em] text-primary-600">COLUMNS</span>
                <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">五个栏目，五种看法</h2>
              </div>
              <p className="max-w-lg text-sm leading-7 text-foreground-600">
                每个栏目对应一种分析问题的角度。先选角度，再看事件，结论会稳得多。
              </p>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {categories.map((cat, index) => (
                <Link
                  key={cat.slug}
                  to={`/topics?category=${cat.slug}`}
                  className={`group flex cursor-pointer flex-col rounded-lg border p-6 transition-colors ${
                    index === 0
                      ? "border-primary-300 bg-primary-50 hover:border-primary-400"
                      : "border-background-200 bg-background-50 hover:border-secondary-400"
                  }`}
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-background-100">
                    <i className={`${cat.icon} text-2xl text-secondary-700`}></i>
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950 group-hover:text-primary-700">
                    {cat.name}
                  </h3>
                  <p className="mt-3 flex-1 text-sm leading-7 text-foreground-600">{cat.desc}</p>
                  <span className="mt-5 flex items-center gap-1 text-xs font-medium text-primary-600">
                    查看该栏目 <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* 最新解读 */}
        <section className="w-full bg-background-50 py-16 md:py-20">
          <div className="mx-auto w-full px-4 md:px-8">
            <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <span className="text-xs font-medium tracking-[0.24em] text-primary-600">LATEST</span>
                <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">最新解读</h2>
              </div>
              <Link
                to="/topics"
                className="flex w-fit cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                查看全部解读 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {latest.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* 读者口碑 */}
        <section className="w-full bg-background-100 py-16 md:py-20">
          <div className="mx-auto w-full px-4 md:px-8">
            <div className="mb-10 text-center">
              <span className="text-xs font-medium tracking-[0.24em] text-primary-600">READERS</span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">读者怎么说</h2>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {testimonials.map((item) => (
                <figure key={item.name} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-6">
                  <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
                  <blockquote className="mt-4 flex-1 text-sm leading-8 text-foreground-700">{item.text}</blockquote>
                  <figcaption className="mt-6 flex items-center gap-3 border-t border-background-200 pt-5">
                    <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-background-200">
                      <img src={item.avatar} alt={`${item.name}的头像`} className="h-full w-full object-cover object-top" />
                    </span>
                    <span className="flex flex-col">
                      <span className="font-heading text-sm font-bold text-foreground-900">{item.name}</span>
                      <span className="text-xs text-foreground-500">{item.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* 订阅 */}
        <section id="subscribe" className="w-full bg-background-50 py-16 md:py-20">
          <div className="mx-auto w-full px-4 md:px-8">
            <div className="rounded-lg border border-primary-200 bg-primary-50 p-7 md:p-12">
              <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
                <div>
                  <span className="flex w-fit items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                    <i className="ri-notification-3-line"></i>每周更新提醒
                  </span>
                  <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                    订阅「黑料吃话」情报
                  </h2>
                  <p className="mt-4 max-w-lg text-sm leading-7 text-foreground-600">
                    新解读上线时给你一封提醒邮件，包含本期核心时间线与三个观察切入点。不发广告，随时可退订。
                  </p>
                </div>
                <NewsletterForm />
              </div>
            </div>
          </div>
        </section>

        {/* 常见问题预览 */}
        <section className="w-full bg-background-100 py-16 md:py-20">
          <div className="mx-auto w-full px-4 md:px-8">
            <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <span className="text-xs font-medium tracking-[0.24em] text-primary-600">FAQ</span>
                <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">你可能想知道</h2>
              </div>
              <Link
                to="/faq"
                className="flex w-fit cursor-pointer items-center gap-2 whitespace-nowrap text-sm font-medium text-secondary-700 transition-colors hover:text-primary-600"
              >
                查看全部问题 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <FaqAccordion items={faqs.slice(0, 4)} />
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}